import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Loader2, ChevronDown, ChevronRight, AlertTriangle, Building2, Copy } from "lucide-react";

// Admin triage for browser-lane apply FAILURES. The extension auto-reports a PII-safe bundle on any failure
// (POST /apply/ext/diag); the backend clusters them by a stable error signature so ONE systemic bug across
// many employers collapses to a single ranked row. This view = the fix-list: attack the highest-count
// signatures, and grab a HAR for the employer shown when a fix needs the successful request contract.

const ATS_COLOR = {
  smartrecruiters: "#0EA5E9", ashby: "#6366F1", greenhouse: "#10B981",
  lever: "#8B5CF6", icims: "#F59E0B",
};
const relTime = (iso) => {
  if (!iso) return "—";
  const s = Math.max(1, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};
const copy = (v) => { navigator.clipboard?.writeText(typeof v === "string" ? v : JSON.stringify(v, null, 2)); toast.success("Copied"); };

function StatusPill({ code }) {
  if (code == null) return null;
  const c = code >= 500 ? "#DC2626" : code >= 400 ? "#D97706" : "#059669";
  return <span className="font-mono text-[11px] px-1.5 py-0.5 rounded" style={{ background: c + "14", color: c }}>HTTP {code}</span>;
}

function Detail({ d }) {
  if (!d) return <div className="text-xs text-muted-foreground">No sample detail.</div>;
  const filled = (d.page1_fields || []);
  const nFilled = filled.filter((f) => f.filled).length;
  const Row = ({ k, children }) => (
    <div className="grid grid-cols-[110px_1fr] gap-2 py-1 border-b border-slate-100 last:border-0">
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground pt-0.5">{k}</div>
      <div className="text-xs text-[#0F172A] min-w-0 break-words">{children}</div>
    </div>
  );
  return (
    <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3">
      {d.ver && <Row k="ext ver">{d.ver}</Row>}
      {d.dc && <Row k="dc">{d.dc}</Row>}
      {d.slug && <Row k="slug / pub">{d.slug}{d.pub_id ? ` · ${d.pub_id}` : ""}</Row>}
      {d.loc_field && <Row k="loc field">{d.loc_field}</Row>}
      {typeof d.answers_count === "number" && <Row k="answers">{d.answers_count} sent</Row>}
      {filled.length > 0 && <Row k="page 1">{nFilled}/{filled.length} fields filled · {filled.map((f) => f.label).filter(Boolean).slice(0, 8).join(", ")}</Row>}
      {d.resume_err && <Row k="résumé err">{typeof d.resume_err === "string" ? d.resume_err : (d.resume_err.attempts || []).join(", ")}</Row>}
      {(d.company_calls || d.api_calls) && <Row k="api calls"><span className="font-mono text-[11px]">{(d.company_calls || d.api_calls || []).slice(0, 16).join("  ·  ")}</span></Row>}
      {d.resp && <Row k="response"><span className="font-mono text-[11px]">{(d.resp.rawText || d.resp.message || JSON.stringify(d.resp)).slice(0, 220)}</span></Row>}
      {d.url && <Row k="url"><span className="font-mono text-[11px] break-all">{d.url}</span></Row>}
      <div className="pt-2">
        <button onClick={() => copy(d)} className="text-[11px] font-mono text-[#0025D8] hover:underline inline-flex items-center gap-1">
          <Copy className="w-3 h-3" /> copy full bundle
        </button>
      </div>
    </div>
  );
}

// The individual failures behind a signature. Each row is one real job you can open, reproduce and
// re-run. The URL captured at failure time is a mid-flow page with dead csrf/hashed tokens, so the
// JOB link here comes from the draft — that is the one worth clicking.
function Occurrences({ signature, days, onRetried }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState("");

  useEffect(() => {
    let live = true;
    api.get(`/admin/ats-diag/occurrences?signature=${encodeURIComponent(signature)}&days=${days}`)
      .then(({ data }) => live && setRows(data.occurrences || []))
      .catch(() => live && setRows([]));
    return () => { live = false; };
  }, [signature, days]);

  const copyReport = (o) => {
    const text = [
      `signature : ${signature}`,
      `ats/step  : ${o.ats} / ${o.step}${o.http_status ? ` (HTTP ${o.http_status})` : ""}`,
      `employer  : ${o.company}${o.title ? ` — ${o.title}` : ""}`,
      `job url   : ${o.job_url || "(unknown)"}`,
      `failed at : ${o.failed_at_url || "(n/a)"}`,
      `draft     : ${o.draft_id}   status: ${o.status}`,
      `candidate : ${o.candidate}${o.candidate_email ? ` <${o.candidate_email}>` : ""}`,
      `when      : ${o.at}`,
      `error     : ${o.error || ""}`,
    ].join("\n");
    navigator.clipboard?.writeText(text);
    setCopied(o.draft_id + o.at); setTimeout(() => setCopied(""), 1200);
  };

  const retry = async (o) => {
    setBusy(o.draft_id);
    try {
      const { data } = await api.post("/admin/ats-diag/retry", { draft_id: o.draft_id });
      toast.success(`Re-queued as ${data.status}`);
      onRetried && onRetried();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not re-queue this application");
    } finally { setBusy(""); }
  };

  if (rows === null) return <div className="text-[11px] text-muted-foreground py-3">Loading occurrences…</div>;
  if (!rows.length) return <div className="text-[11px] text-muted-foreground py-3">No occurrences in this window.</div>;
  return (
    <div className="mt-3 rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
      {rows.map((o) => (
        <div key={o.draft_id + o.at} className="p-3 bg-white">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className="text-[12px] font-semibold text-[#0F172A]">{o.candidate || "(unknown candidate)"}</span>
            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{o.draft_id}</span>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
              ["submitted", "confirmed"].includes(o.status) ? "bg-emerald-50 text-emerald-700"
                : o.status === "failed" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
              {o.status}
            </span>
            {o.applied_manually && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700"
                title="The candidate applied to this one by hand — retrying would duplicate it">
                applied manually
              </span>
            )}
            <span className="ml-auto font-mono text-[10px] text-muted-foreground">{relTime(o.at)}</span>
          </div>
          {o.job_url && (
            <a href={o.job_url} target="_blank" rel="noreferrer"
              className="font-mono text-[11px] text-[#0025D8] hover:underline break-all block mb-1">
              {o.job_url}
            </a>
          )}
          <div className="flex items-center gap-2 mt-2">
            <button onClick={() => copyReport(o)}
              className="text-[11px] font-semibold px-2 py-1 rounded border border-slate-200 text-slate-700 hover:bg-slate-50">
              {copied === o.draft_id + o.at ? "Copied ✓" : "Copy report"}
            </button>
            <a href={`/admin/support?user=${encodeURIComponent(o.user_id || "")}&draft=${encodeURIComponent(o.draft_id)}`}
              className="text-[11px] font-semibold px-2 py-1 rounded border border-slate-200 text-slate-700 hover:bg-slate-50">
              Message candidate
            </a>
            <button onClick={() => retry(o)} disabled={!o.retryable || busy === o.draft_id}
              title={o.applied_manually ? "The candidate applied manually — retry is blocked"
                : !o.retryable ? "Nothing to retry" : "Re-run this application after your fix"}
              className="text-[11px] font-semibold px-2 py-1 rounded border border-[#0025D8]/30 text-[#0025D8] hover:bg-[#0025D8]/[0.05] disabled:opacity-40 disabled:cursor-not-allowed">
              {busy === o.draft_id ? "Re-queueing…" : "Retry"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function SignatureCard({ row, days, onChanged }) {
  const [open, setOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [, step, , ...errParts] = (row.signature || "").split("|");
  const errText = errParts.join("|") || "error";
  const ats = row.ats || "?";

  const toggleResolved = async (e) => {
    e.stopPropagation();                      // the header is itself the expand/collapse control
    setResolving(true);
    try {
      await api.post("/admin/ats-diag/resolve", { signature: row.signature, resolved: !row.resolved_at });
      toast.success(row.resolved_at ? "Reopened" : "Marked resolved");
      onChanged && onChanged();
    } catch { toast.error("Could not update"); } finally { setResolving(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-4 p-4 text-left hover:bg-slate-50 transition-colors">
        <div className="flex flex-col items-center justify-center w-14 flex-shrink-0">
          <div className="text-2xl font-bold leading-none text-[#0F172A]">{row.count}</div>
          <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground mt-0.5">reports</div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: ATS_COLOR[ats] || "#64748B" }}>{ats}</span>
            <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{step || "?"}</span>
            <StatusPill code={row.http_status} />
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Building2 className="w-3 h-3" />{row.company_count} employer{row.company_count === 1 ? "" : "s"}</span>
          </div>
          <div className="text-sm text-[#0F172A] truncate">{errText}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {row.recurred && (
            <span className="text-[10px] font-bold text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded"
              title="Happened again AFTER it was marked resolved">recurred</span>
          )}
          {row.resolved_at && !row.recurred && (
            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">resolved</span>
          )}
          {/* Visible without expanding — the whole point is to triage the list at a glance. */}
          <span role="button" tabIndex={0} onClick={toggleResolved}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggleResolved(e); }}
            aria-disabled={resolving}
            title={row.resolved_at ? "Put this back in the open list" : "You've fixed this — hide it until it happens again"}
            className={`text-[11px] font-semibold px-2 py-1 rounded border transition-colors ${
              resolving ? "opacity-40" : "hover:bg-slate-50"} ${
              row.resolved_at ? "border-slate-200 text-slate-600" : "border-emerald-200 text-emerald-700"}`}>
            {row.resolved_at ? "Reopen" : "Resolve"}
          </span>
          <span className="text-[11px] font-mono text-muted-foreground">{relTime(row.last_seen)}</span>
          {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4">
          {(row.companies || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-1">
              {row.companies.map((c) => (
                <span key={c} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{c}</span>
              ))}
            </div>
          )}
          <div className="font-mono text-[10px] text-slate-400 break-all pt-1">{row.signature}</div>
          <Detail d={row.sample_detail} />
          <Occurrences signature={row.signature} days={days} onRetried={onChanged} />
          {row.resolved_at && (
            <div className="text-[11px] text-muted-foreground mt-3">
              Marked resolved {relTime(row.resolved_at)}
              {row.recurred && <span className="text-rose-700 font-semibold"> — but it has happened again since.</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminAtsDiagnostics() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [ats, setAts] = useState("");
  const [company, setCompany] = useState("");
  const [showResolved, setShowResolved] = useState(false);
  const [facets, setFacets] = useState({ ats: [], companies: [] });

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams({ days: String(days) });
    if (ats) qs.set("ats", ats);
    if (company) qs.set("company", company);
    if (showResolved) qs.set("include_resolved", "1");
    api.get(`/apply/ext/diag/summary?${qs}`)
      .then(({ data }) => { setRows(data.signatures || []); setFacets(data.filters || { ats: [], companies: [] }); })
      .catch(() => toast.error("Could not load apply diagnostics"))
      .finally(() => setLoading(false));
  }, [days, ats, company, showResolved]);
  useEffect(() => { load(); }, [load]);

  // Viewing the diagnostics clears the Admin dashboard's "new Workday failures" badge.
  useEffect(() => { api.post("/admin/ats-diag/mark-seen").catch(() => {}); }, []);

  const totalReports = rows.reduce((s, r) => s + (r.count || 0), 0);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#0025D8] mb-6">
        <ArrowLeft className="w-4 h-4" /> Admin
      </Link>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-semibold text-[#0F172A] flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#D97706]" /> ATS Apply Diagnostics
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Browser-lane apply failures, clustered by cause. Highest-count first — that's the fix that unblocks the most employers.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-[#0F172A]">
            <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
          </select>
          <select value={ats} onChange={(e) => setAts(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-[#0F172A]">
            <option value="">All ATS</option>
            {facets.ats.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={company} onChange={(e) => setCompany(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-[#0F172A] max-w-[180px]">
            <option value="">All employers</option>
            {facets.companies.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap"
            title="Resolved signatures are hidden unless they have happened again since the fix">
            <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
            Show resolved
          </label>
          <button onClick={load} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" title="Refresh">
            <RefreshCw className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>

      <div className="flex gap-3 mb-5">
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Distinct causes</div>
          <div className="text-xl font-bold text-[#0F172A]">{rows.length}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Total failure reports</div>
          <div className="text-xl font-bold text-[#0F172A]">{totalReports}</div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-3xl mb-2">🎉</div>
          <p className="text-sm text-muted-foreground">No apply failures reported in the last {days} days.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {rows.map((r) => <SignatureCard key={r.signature} row={r} days={days} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}
